import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Share2,
  Link,
  Copy,
  Check,
  Mail,
  Send,
  Loader2,
  RefreshCw,
  Eye,
  Globe,
  Lock
} from 'lucide-react';

const ShareMealPlanDialog = ({ open, onClose }) => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [shareStatus, setShareStatus] = useState(null);
  const [enabling, setEnabling] = useState(false);
  const [copied, setCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  
  // Email form
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailData, setEmailData] = useState({
    recipient_email: '',
    recipient_name: '',
    message: ''
  });
  const [sendingEmail, setSendingEmail] = useState(false);

  // Load share status
  useEffect(() => {
    const loadStatus = async () => {
      if (!open) return;
      
      setLoading(true);
      try {
        const response = await api.get('/meal-plans/share/status');
        setShareStatus(response.data);
      } catch (error) {
        console.error('Failed to load share status:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadStatus();
  }, [api, open]);

  const toggleSharing = async (enabled) => {
    setEnabling(true);
    try {
      const response = await api.post('/meal-plans/share', { enabled });
      setShareStatus(response.data);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update sharing');
    } finally {
      setEnabling(false);
    }
  };

  const copyLink = async () => {
    if (!shareStatus?.share_url) return;
    
    try {
      await navigator.clipboard.writeText(shareStatus.share_url);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const regenerateLink = async () => {
    setRegenerating(true);
    try {
      const response = await api.post('/meal-plans/share/regenerate');
      setShareStatus(prev => ({
        ...prev,
        share_id: response.data.share_id,
        share_url: response.data.share_url
      }));
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to regenerate link');
    } finally {
      setRegenerating(false);
    }
  };

  const sendEmailInvite = async () => {
    if (!emailData.recipient_email) {
      toast.error('Please enter an email address');
      return;
    }
    
    setSendingEmail(true);
    try {
      const response = await api.post('/meal-plans/share/email', emailData);
      toast.success(response.data.message);
      setShowEmailForm(false);
      setEmailData({ recipient_email: '', recipient_name: '', message: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-['Playfair_Display'] flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            Share Meal Plan
          </DialogTitle>
          <DialogDescription>
            Share your weekly meal plan with family and friends
          </DialogDescription>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : !shareStatus?.enabled ? (
          // Sharing disabled state
          <div className="py-6 text-center">
            <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
              <Lock className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="font-semibold mb-2">Your meal plan is private</h3>
            <p className="text-sm text-muted-foreground mb-6">
              Enable sharing to get a link you can share with anyone
            </p>
            <Button onClick={() => toggleSharing(true)} disabled={enabling} data-testid="enable-sharing-btn">
              {enabling ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Globe className="h-4 w-4 mr-2" />
              )}
              Enable Sharing
            </Button>
          </div>
        ) : (
          // Sharing enabled state
          <div className="space-y-4 py-4">
            {/* Share Link */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Link className="h-4 w-4" />
                Shareable Link
              </Label>
              <div className="flex gap-2">
                <Input
                  value={shareStatus.share_url || ''}
                  readOnly
                  className="flex-1 text-sm bg-muted"
                  data-testid="share-url-input"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={copyLink}
                  data-testid="copy-link-btn"
                >
                  {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Eye className="h-3 w-3" />
                  {shareStatus.view_count || 0} views
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-xs"
                  onClick={regenerateLink}
                  disabled={regenerating}
                >
                  {regenerating ? (
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3 w-3 mr-1" />
                  )}
                  New Link
                </Button>
              </div>
            </div>
            
            {/* Email Invite */}
            <div className="pt-4 border-t">
              {!showEmailForm ? (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setShowEmailForm(true)}
                  data-testid="email-invite-btn"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Send via Email
                </Button>
              ) : (
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label>Recipient Email *</Label>
                    <Input
                      type="email"
                      placeholder="friend@email.com"
                      value={emailData.recipient_email}
                      onChange={(e) => setEmailData(prev => ({ ...prev, recipient_email: e.target.value }))}
                      data-testid="recipient-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Their Name (optional)</Label>
                    <Input
                      placeholder="Friend's name"
                      value={emailData.recipient_name}
                      onChange={(e) => setEmailData(prev => ({ ...prev, recipient_name: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Personal Message (optional)</Label>
                    <Input
                      placeholder="Check out my meal plan for this week!"
                      value={emailData.message}
                      onChange={(e) => setEmailData(prev => ({ ...prev, message: e.target.value }))}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setShowEmailForm(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="flex-1"
                      onClick={sendEmailInvite}
                      disabled={sendingEmail}
                      data-testid="send-email-btn"
                    >
                      {sendingEmail ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4 mr-2" />
                      )}
                      Send
                    </Button>
                  </div>
                </div>
              )}
            </div>
            
            {/* Disable Sharing */}
            <div className="pt-4 border-t">
              <Button
                variant="ghost"
                className="w-full text-muted-foreground hover:text-destructive"
                onClick={() => toggleSharing(false)}
                disabled={enabling}
              >
                <Lock className="h-4 w-4 mr-2" />
                Disable Sharing
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ShareMealPlanDialog;
